import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Content from "../../../components/Content";
import MovementMap from "../../../components/movements/MovementMap";
import MovementStatCards from "../../../components/movements/MovementStatCards";
import MovementTimeline from "../../../components/movements/MovementTimeline";
import MovementStopList from "../../../components/movements/MovementStopList";
import MovementDateRange from "../../../components/movements/MovementDateRange";
import { useUserMovements } from "../../../hooks/useUserMovements";
import { useReverseGeocoded } from "../../../hooks/useReverseGeocoded";
import { GetUserById } from "../../../hooks/useUserData";
import {
  detectStops,
  sortByRecordedAt,
  splitIntoSegments,
  summarizeMovements,
} from "../../../utils/locationAnalytics";

const INITIAL_PRESET_HOURS = 24;

const buildInitialRange = (hours) => {
  const now = new Date();
  const from = new Date(now.getTime() - hours * 3600 * 1000);
  return {
    fromISO: from.toISOString(),
    toISO: now.toISOString(),
    presetHours: hours,
  };
};

export const UserMovementsView = () => {
  const { id: userIdParam } = useParams();
  const userId = userIdParam ?? null;
  const navigate = useNavigate();

  const { data: userData, isLoading: userLoading } = GetUserById(userId);
  const user = userData?.data;

  const [range, setRange] = useState(() => buildInitialRange(INITIAL_PRESET_HOURS));
  const [playheadIndex, setPlayheadIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeStopIndex, setActiveStopIndex] = useState(null);

  const {
    data: rawPoints = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useUserMovements(userId, range.fromISO, range.toISO);

  const points = useMemo(() => sortByRecordedAt(rawPoints), [rawPoints]);
  const summary = useMemo(() => summarizeMovements(points), [points]);
  const stops = useMemo(
    () => detectStops(points, { radiusMeters: 35, minDwellMs: 2 * 60 * 1000 }),
    [points]
  );
  const segments = useMemo(
    () => splitIntoSegments(points, stops),
    [points, stops]
  );

  // Coordinates we actually want street-level names for: start, end, and
  // every detected stop. We deliberately skip in-between points — nominatim
  // is rate-limited to 1 req/s and the path itself doesn't need labels.
  const geocodeCoords = useMemo(() => {
    const list = [];
    if (points.length > 0) {
      list.push({
        lat: points[0].latitude,
        lng: points[0].longitude,
      });
      list.push({
        lat: points[points.length - 1].latitude,
        lng: points[points.length - 1].longitude,
      });
    }
    stops.forEach((s) => {
      list.push({ lat: s.center.latitude, lng: s.center.longitude });
    });
    return list;
  }, [points, stops]);

  const { getLabel } = useReverseGeocoded(geocodeCoords);

  // Reset the scrubber whenever the underlying point window changes.
  useEffect(() => {
    setPlayheadIndex(0);
    setIsPlaying(false);
    setActiveStopIndex(null);
  }, [points.length, range.fromISO, range.toISO]);

  // Stop auto-playback once it reaches the end.
  useEffect(() => {
    if (!isPlaying) return;
    if (playheadIndex >= points.length - 1) {
      setIsPlaying(false);
    }
  }, [isPlaying, playheadIndex, points.length]);

  const handleSelectStop = useCallback(
    (stop, idx) => {
      const index = typeof idx === "number" ? idx : stops.indexOf(stop);
      if (index < 0) return;
      setActiveStopIndex(index);
      setPlayheadIndex(stops[index].startIndex);
      setIsPlaying(false);
    },
    [stops]
  );

  const handleIndexChange = useCallback((updater) => {
    setPlayheadIndex((prev) =>
      typeof updater === "function" ? updater(prev) : updater
    );
  }, []);

  const fullName =
    user && (user.first_name || user.last_name)
      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
      : "Användare";

  const subtitle = user?.email || "—";
  const groupName = user?.user_group?.group_name || user?.group?.group_name;

  return (
    <Content title={`Rörelseanalys — ${fullName}`}>
      <Box className="movements-page">
        {/* Header strip ------------------------------------------------- */}
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                  onClick={() => navigate(-1)}
                  size="small"
                  sx={{
                    bgcolor: "rgba(16,50,74,0.06)",
                    "&:hover": { bgcolor: "rgba(16,50,74,0.12)" },
                  }}
                  aria-label="Gå tillbaka"
                >
                  <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: '"Outfit", "DM Sans", sans-serif',
                      fontWeight: 700,
                      color: "#10324a",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {userLoading ? "Laddar användare…" : fullName}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.3}
                  >
                    <Typography
                      sx={{
                        color: "#7a8294",
                        fontSize: 13,
                      }}
                    >
                      {subtitle}
                    </Typography>
                    {groupName && (
                      <Chip
                        label={groupName}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          background: "rgba(138,17,37,0.08)",
                          color: "#8a1125",
                          border: "1px solid rgba(138,17,37,0.2)",
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {(isLoading || isFetching) && (
                  <CircularProgress size={18} sx={{ color: "#8a1125" }} />
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => refetch()}
                  startIcon={<RefreshRoundedIcon />}
                  disabled={isFetching}
                >
                  Uppdatera
                </Button>
              </Stack>
            </Stack>

            <Box mt={2.5}>
              <MovementDateRange
                fromISO={range.fromISO}
                toISO={range.toISO}
                activePresetHours={range.presetHours}
                onChange={setRange}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Error state -------------------------------------------------- */}
        {isError && (
          <Alert severity="error" variant="outlined">
            Kunde inte hämta rörelsedata
            {error?.response?.data?.error?.message
              ? ` — ${error.response.data.error.message}`
              : error?.message
              ? ` — ${error.message}`
              : "."}
          </Alert>
        )}

        {/* Stat cards --------------------------------------------------- */}
        <MovementStatCards summary={summary} stopCount={stops.length} />

        {/* Map + side panel -------------------------------------------- */}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <MovementMap
              points={points}
              segments={segments}
              stops={stops}
              playheadIndex={playheadIndex}
              onSelectStop={(stop) => {
                const idx = stops.indexOf(stop);
                handleSelectStop(stop, idx);
              }}
              getLabel={getLabel}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <MovementStopList
              stops={stops}
              onSelectStop={handleSelectStop}
              activeStopIndex={activeStopIndex}
              getLabel={getLabel}
            />
          </Grid>
        </Grid>

        {/* Timeline scrubber ------------------------------------------- */}
        {points.length > 0 && (
          <MovementTimeline
            points={points}
            index={playheadIndex}
            onIndexChange={handleIndexChange}
            isPlaying={isPlaying}
            onPlayPause={() => setIsPlaying((p) => !p)}
          />
        )}
      </Box>
    </Content>
  );
};

export default UserMovementsView;
